import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { RootState } from "../../../reducers";
import { IInitialState, IResponse } from "../../../shared/shared-interfaces";

import {
  createEntity,
  getEntities,
  getEntity,
  IOrderParams,
  removeEntity,
  updateDeleteEntity,
  updateEntity,
} from "./orderManagement.api";
import { IOrder } from "@/shared/model/order.model";

interface IOrdersInitialState extends IInitialState {
  updateEntityStatusSuccess: boolean;
  filterState: IOrderParams;
  loading: boolean;
}

export const initialordersFilter: IOrderParams = {
  page: 1,
  limit: 10,
  sortBy: "id",
  sortOrder: "DESC",
};

const initialState: IOrdersInitialState = {
  fetchEntitiesSuccess: false,
  fetchEntitySuccess: false,
  updateEntitySuccess: false,
  deleteEntitySuccess: false,
  loading: false,
  errorMessage: null,
  totalItems: 0,
  totalPages: 0,
  updateEntityStatusSuccess: false,
  filterState: initialordersFilter,
};

export const ordersAdapter = createEntityAdapter<IOrder>({
  selectId: ({ id }) => id,
});

const { actions, reducer } = createSlice({
  name: "ordersSlice",
  initialState: ordersAdapter.getInitialState({ initialState }),
  reducers: {
    setFilterState(state, { payload }: PayloadAction<IOrderParams>) {
      state.initialState.filterState = payload;
    },
    fetching(state) {
      state.initialState.loading = true;
    },
    resetAll(state) {
      state.initialState.loading = false;
      state.initialState.fetchEntitiesSuccess = false;
      state.initialState.fetchEntitySuccess = false;
      state.initialState.updateEntitySuccess = false;
      state.initialState.deleteEntitySuccess = false;
      state.initialState.errorMessage = null;
    },
    resetEntity(state) {
      state.initialState.updateEntitySuccess = false;
      state.initialState.errorMessage = null;
      state.initialState.deleteEntitySuccess = false;
    },
    addItem(state, { payload }: PayloadAction<IOrder>) {
      if (!state.ids.includes(payload.id)) {
        ordersAdapter.addOne(state, payload);
        state.ids = [payload.id, ...state.ids];
      }
    },
    updateItem(
      state,
      { payload }: PayloadAction<Partial<IOrder> & { id: string }>
    ) {
      console.log(payload, 'payloadpayloadpayload')
      ordersAdapter.updateOne(state, {
        id: payload.id,
        changes: { ...payload },
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getEntities.fulfilled.type,
      (
        state,
        { payload }: PayloadAction<AxiosResponse<IResponse<IOrder[]>>>
      ) => {
        ordersAdapter.setAll(state, payload.data.data);
        state.initialState.totalItems = Number(payload.data.count);
        state.initialState.totalPages = Number(payload.data.total_pages);
        state.initialState.fetchEntitiesSuccess = true;
        state.initialState.loading = false;
      }
    );
    builder.addCase(
      getEntities.rejected.type,
      (state, { payload }: PayloadAction<any>) => {
        state.initialState.errorMessage = payload?.message;
        state.initialState.loading = false;
        state.initialState.fetchEntitiesSuccess = false;
      }
    );

    builder.addCase(
      getEntity.fulfilled.type,
      (state, { payload }: PayloadAction<IOrder>) => {
        ordersAdapter.upsertOne(state, payload);
        state.initialState.fetchEntitySuccess = true;
        state.initialState.loading = false;
      }
    );
    builder.addCase(
      getEntity.rejected.type,
      (state, { payload }: PayloadAction<any>) => {
        state.initialState.errorMessage = payload?.message;
        state.initialState.loading = false;
        state.initialState.fetchEntitySuccess = false;
      }
    );
    builder.addCase(
      updateEntity.fulfilled.type,
      (state, { payload }: PayloadAction<IOrder>) => {
        ordersAdapter.updateOne(state, { id: payload.id, changes: payload });
        state.initialState.updateEntitySuccess = true;
        state.initialState.loading = false;
      }
    );
    builder.addCase(
      updateEntity.rejected.type,
      (state, { payload }: PayloadAction<any>) => {
        state.initialState.errorMessage = payload?.message;
        state.initialState.loading = false;
        state.initialState.updateEntitySuccess = false;
      }
    );
    builder.addCase(
      createEntity.fulfilled.type,
      (state, { payload }: PayloadAction<IOrder>) => {
        ordersAdapter.addOne(state, payload);
        state.initialState.updateEntitySuccess = true;
        state.initialState.loading = false;
      }
    );
    builder.addCase(
      createEntity.rejected.type,
      (state, { payload }: PayloadAction<any>) => {
        state.initialState.errorMessage = payload?.message;
        state.initialState.loading = false;
        state.initialState.updateEntitySuccess = false;
      }
    );
    builder.addCase(
      removeEntity.fulfilled.type,
      (state, { payload }: PayloadAction<string>) => {
        ordersAdapter.removeOne(state, payload);
        state.initialState.totalItems -= 1;
        state.initialState.deleteEntitySuccess = true;
        state.initialState.loading = false;
      }
    );
    builder.addCase(
      removeEntity.rejected.type,
      (state, { payload }: PayloadAction<any>) => {
        state.initialState.errorMessage = payload?.message;
        state.initialState.loading = false;
        state.initialState.deleteEntitySuccess = false;
      }
    );
    builder.addCase(
      updateDeleteEntity.fulfilled.type,
      (state, { payload }: PayloadAction<string[]>) => {
        ordersAdapter.removeMany(state, payload);
        state.initialState.totalItems -= payload.length;
        state.initialState.deleteEntitySuccess = true;
        state.initialState.loading = false;
      }
    );
    builder.addCase(
      updateDeleteEntity.rejected.type,
      (state, { payload }: PayloadAction<any>) => {
        state.initialState.errorMessage = payload?.message;
        state.initialState.loading = false;
        state.initialState.deleteEntitySuccess = false;
      }
    );
  },
});

export const {
  fetching,
  resetAll,
  resetEntity,
  setFilterState,
  addItem,
  updateItem,
} = actions;
export default reducer;

export const orderSelectors = ordersAdapter.getSelectors<RootState>(
  (state) => state.ordersReducer
);

const { selectById } = ordersAdapter.getSelectors();
const getorderstate = (rootState: RootState) => rootState.ordersReducer;

export const selectEntityById = (id: string) => {
  return createSelector(getorderstate, (state) => selectById(state, id));
};
